import type {
  ChildDevice,
  ChildProfile,
  InternetAccessAccount,
  PairingCode,
} from '../../api/types.js';

/**
 * One-to-one port of the Android app's `ParentDashboardUiState` — same
 * field names, same meanings. Keeping them identical makes the two
 * clients' behaviour comparable at a glance when one of them misbehaves.
 */
export interface ParentDashboardState {
  isLoading: boolean;
  childProfiles: ChildProfile[];
  newChildName: string;
  isCreatingChild: boolean;
  pendingPairingCodeChildId: string | null;
  pairingCodesByChildId: Record<string, PairingCode>;
  cancellingPairingCodeChildId: string | null;
  /**
   * Devices paired to each child — a child can have more than one (phone
   * + tablet, etc.); they all share the same quota, since that is scoped
   * to the child profile, not the device.
   */
  devicesByChildId: Record<string, ChildDevice[]>;
  revokingDeviceId: string | null;
  deletingChildProfileId: string | null;
  /**
   * Internet-Access (see docs/INTERNET_ACCESS.md) — a missing entry means
   * "not loaded yet"; a child with no top-ups yet still gets a real
   * account with a zero balance, not a missing one.
   */
  internetAccessAccountByChildId: Record<string, InternetAccessAccount>;
  savingTopUpChildId: string | null;
  savingDeductBonusChildId: string | null;
  savingForceBlockedChildId: string | null;
  savingResetChildId: string | null;
  /** Adding or removing a Sperrzeit — one marker for both, like Android. */
  savingBlockWindowChildId: string | null;
  errorMessage: string | null;
}

export const initialParentDashboardState: ParentDashboardState = {
  isLoading: true,
  childProfiles: [],
  newChildName: '',
  isCreatingChild: false,
  pendingPairingCodeChildId: null,
  pairingCodesByChildId: {},
  cancellingPairingCodeChildId: null,
  devicesByChildId: {},
  revokingDeviceId: null,
  deletingChildProfileId: null,
  internetAccessAccountByChildId: {},
  savingTopUpChildId: null,
  savingDeductBonusChildId: null,
  savingForceBlockedChildId: null,
  savingResetChildId: null,
  savingBlockWindowChildId: null,
  errorMessage: null,
};

/** Removes one key from a record without mutating it — the `map - key` of the Kotlin original. */
export function withoutKey<T>(record: Record<string, T>, key: string): Record<string, T> {
  const { [key]: _removed, ...rest } = record;
  return rest;
}
